import { Suspense, useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import {
  Squares2X2Icon,
  UsersIcon,
  BriefcaseIcon,
  ChartBarIcon,
  SparklesIcon,
  Cog6ToothIcon,
  SignalIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Navbar from "./Navbar";
import ScrollToTopButton from "./ScrollToTopButton";
import UsaXSpinner from "../ui/UsaXSpinner";
import { useAuth } from "../../contexts/AuthContext";

const SIDEBAR_COLLAPSED_KEY = "app-sidebar-collapsed";

const links = [
  {
    title: "Dashboard",
    role: ["SuperAdmin", "Admin", "Driver", "Dispatcher"],
    icon: Squares2X2Icon,
    link: "/",
  },
  {
    title: "Bids",
    role: ["SuperAdmin", "Admin", "Dispatcher", "Driver"],
    icon: BriefcaseIcon,
    link: "/bids",
  },
  {
    title: "Market Condition",
    role: ["SuperAdmin", "Admin", "Dispatcher", "Driver"],
    icon: ChartBarIcon,
    link: "/market-condition",
  },
  {
    title: "Parade",
    role: ["SuperAdmin", "Admin", "Dispatcher", "Driver"],
    icon: SparklesIcon,
    link: "/parade",
  },
  {
    title: "Settings",
    role: ["SuperAdmin", "Admin", "Dispatcher", "Driver"],
    icon: Cog6ToothIcon,
    link: "/settings",
  },
  {
    title: "Admin",
    role: ["SuperAdmin", "Admin"],
    children: [
      {
        title: "Users",
        role: ["SuperAdmin", "Admin"],
        icon: UsersIcon,
        link: "/admin/users",
      },
      {
        title: "Health",
        role: ["SuperAdmin", "Admin"],
        icon: SignalIcon,
        link: "/admin/health",
      },
    ],
  },
];

function SidebarPanel({
  collapsed,
  setCollapsed,
  pathname,
  userType,
  onNavigate,
  forceExpandedLabels,
  showCloseButton,
  showMinimize,
}) {
  return (
    <>
      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto px-2 py-3">
        {showCloseButton && (
          <div className="mb-2 flex items-center justify-between px-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-white/70">
              Menu
            </span>
            <button
              type="button"
              onClick={onNavigate}
              className="rounded p-1.5 text-white hover:bg-white/10"
              aria-label="Close menu"
            >
              <XMarkIcon className="size-5" />
            </button>
          </div>
        )}
        <LinkGroup
          links={links}
          activeLink={pathname}
          userType={userType}
          collapsed={collapsed}
          onNavigate={onNavigate}
          forceExpandedLabels={forceExpandedLabels}
        />
      </div>
      {showMinimize && (
        <div className="flex-shrink-0 border-t border-secondary-700 p-2">
          <button
            type="button"
            onClick={() => setCollapsed((prev) => !prev)}
            className={`flex w-full items-center rounded px-2 py-2 text-sm text-white transition-colors hover:bg-white/10 ${
              collapsed ? "justify-center" : "gap-2"
            }`}
            title={collapsed ? "Expand sidebar" : "Minimize sidebar"}
            aria-label={collapsed ? "Expand sidebar" : "Minimize sidebar"}
          >
            {collapsed ? (
              <ChevronDoubleRightIcon className="size-4 flex-shrink-0" />
            ) : (
              <>
                <ChevronDoubleLeftIcon className="size-4 flex-shrink-0" />
                <span>Minimize</span>
              </>
            )}
          </button>
        </div>
      )}
    </>
  );
}

export default function Layout() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isFullBleedPage =
    pathname === "/parade" ||
    pathname === "/market-condition" ||
    pathname.startsWith("/load/");

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      // ignore storage errors
    }
  }, [collapsed]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const previousHtml = html.style.overflow;
    const previousBody = body.style.overflow;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    return () => {
      html.style.overflow = previousHtml;
      body.style.overflow = previousBody;
    };
  }, []);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  const closeMobileNav = () => setMobileNavOpen(false);

  return (
    <main className="h-[100dvh] overflow-hidden">
      <Navbar
        onOpenMobileNav={() => setMobileNavOpen(true)}
        mobileNavOpen={mobileNavOpen}
      />
      <div
        className="flex w-full flex-nowrap"
        style={{
          height: "calc(100dvh - 60px)",
        }}
      >
        <div
          className={`hidden h-full flex-shrink-0 flex-col bg-secondary text-white transition-[width] duration-200 md:flex ${
            collapsed ? "w-14" : "w-60"
          }`}
        >
          <SidebarPanel
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            pathname={pathname}
            userType={user.user_type}
            onNavigate={undefined}
            forceExpandedLabels={false}
            showCloseButton={false}
            showMinimize
          />
        </div>

        {mobileNavOpen && (
          <div className="fixed inset-0 z-[60] md:hidden" role="dialog" aria-modal="true">
            <button
              type="button"
              className="absolute inset-0 bg-slate-900/50"
              aria-label="Close menu overlay"
              onClick={closeMobileNav}
            />
            <div className="absolute left-0 top-0 flex h-full w-[min(18rem,85vw)] flex-col bg-secondary text-white shadow-xl">
              <SidebarPanel
                collapsed={false}
                setCollapsed={setCollapsed}
                pathname={pathname}
                userType={user.user_type}
                onNavigate={closeMobileNav}
                forceExpandedLabels
                showCloseButton
                showMinimize={false}
              />
            </div>
          </div>
        )}

        <div
          className={`flex h-full min-h-0 min-w-0 flex-1 flex-col ${
            isFullBleedPage
              ? "overflow-hidden p-0"
              : "overflow-y-auto overflow-x-hidden p-2 sm:p-3 md:p-4"
          }`}
        >
          <Suspense
            fallback={
              <div className="flex h-full min-h-[40vh] w-full items-center justify-center bg-white">
                <UsaXSpinner />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </div>
      </div>
      <ScrollToTopButton />
    </main>
  );
}

const LinkGroup = ({
  links,
  activeLink,
  userType,
  collapsed,
  onNavigate,
  forceExpandedLabels = false,
}) => {
  const showLabels = forceExpandedLabels || !collapsed;

  return links.map((link) =>
    !link.role || link.role.includes(userType) ? (
      link.children ? (
        <div key={link.title}>
          {showLabels ? (
            <div className="mt-2 border-t border-white/15 px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-white/60">
              {link.title}
            </div>
          ) : (
            <div
              className="mx-2 my-2 border-t border-white/25"
              role="separator"
            />
          )}
          <LinkGroup
            links={link.children}
            activeLink={activeLink}
            userType={userType}
            collapsed={collapsed}
            onNavigate={onNavigate}
            forceExpandedLabels={forceExpandedLabels}
          />
        </div>
      ) : (
        <Link
          to={link.link}
          key={link.title}
          title={link.title}
          onClick={onNavigate}
        >
          <div
            className={`flex items-center rounded-lg text-sm text-white transition-colors my-1 ${
              showLabels
                ? "gap-2 px-3 py-2.5"
                : "justify-center px-2 py-2.5"
            } ${
              (link.link === "/"
                ? activeLink === "/"
                : activeLink === link.link ||
                  activeLink.startsWith(`${link.link}/`))
                ? "bg-primary shadow-sm"
                : "bg-transparent hover:bg-white/10"
            }`}
          >
            <link.icon className="size-4 flex-shrink-0" />
            {showLabels && <span className="truncate">{link.title}</span>}
          </div>
        </Link>
      )
    ) : null
  );
};
